import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  factIdToValue,
  PREK_PRACTICE_TRACKS,
} from '../shared';

interface WhatComesNextIDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

interface NumberSequence {
  id: string;
  targetNumber: number;
  sequence: number[];
}

const WhatComesNextIDoScreen: React.FC<WhatComesNextIDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'showing' | 'repeating' | 'waiting'>('waiting');
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [currentSequence, setCurrentSequence] = useState<NumberSequence | null>(null);
  const [showSequence, setShowSequence] = useState(false);

  const { playTTSWithCallback, isTTSPlaying } = usePreKTTS();

  // Generate number sequence from target (target 3 = [1,2], target 4 = [2,3], target 5 = [3,4])
  const generateSequenceFromTarget = useCallback((targetNumber: number): NumberSequence => {
    const sequence = [targetNumber - 2, targetNumber - 1];

    return {
      id: `sequence-${Date.now()}-${Math.random()}`,
      targetNumber,
      sequence
    };
  }, []);

  const currentFactId = factsToPractice[currentFactIndex];
  const currentTarget = currentFactId
    ? factIdToValue(PREK_PRACTICE_TRACKS.WHATCOMESNEXT, currentFactId)
    : undefined;

  // Update sequence when fact changes
  useEffect(() => {
    // Guard: Don't proceed if no target is available
    if (currentTarget === undefined) {
      return;
    }
    const newSequence = generateSequenceFromTarget(currentTarget);
    setCurrentSequence(newSequence);
  }, [currentTarget, generateSequenceFromTarget]);

  // Initialize to showing phase when component mounts or fact changes
  useEffect(() => {
    setCurrentPhase('showing');
    setShowSequence(false);

    // Small delay then show sequence
    setTimeout(() => {
      setShowSequence(true);
    }, 500);
  }, [currentFactIndex]);

  // Auto-advance to next fact after 3 seconds (longer for sequence comprehension)
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }

    const timer = setTimeout(() => {
      if (currentFactIndex < factsToPractice.length - 1) {
        // Move to next fact
        setCurrentFactIndex(currentFactIndex + 1);
        setCurrentPhase('showing');
        setShowSequence(false);
      } else {
        // All facts completed, move to next screen
        onComplete();
      }
    }, PRACTICE_TIMER_CONSTANTS.WHATCOMESNEXT_AUTO_ADVANCE_MS);

    setAutoAdvanceTimer(timer);
  }, [currentFactIndex, factsToPractice.length, onComplete, autoAdvanceTimer]);

  // Play TTS for current sequence
  useEffect(() => {
    if (currentPhase === 'showing' && showSequence && settings.speechEnabled) {
      // Play specific "What comes next?" instruction for this target number
      playTTSWithCallback(QUESTION_SCREEN_AUDIO.whatComesNextIdo[currentTarget as 3 | 4 | 5 | 6 | 7 | 8 | 9], () => {
        setCurrentPhase('repeating');

        // Start auto-advance immediately after TTS completes
        setCurrentPhase('waiting');
        startAutoAdvance();
      }).catch(() => {
        console.error('❌ WhatComesNextIDoScreen TTS failed');
        // If TTS fails, still advance
        setCurrentPhase('waiting');
        startAutoAdvance();
      });
    } else if (currentPhase === 'showing' && showSequence && !settings.speechEnabled) {
      // If speech is disabled, still show the sequence and auto-advance
      setCurrentPhase('waiting');
      startAutoAdvance();
    }
  }, [currentPhase, currentTarget, settings.speechEnabled, playTTSWithCallback, startAutoAdvance, currentFactIndex, showSequence]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
    };
  }, [autoAdvanceTimer]);

  if (!currentSequence) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  // Loading state: show loading screen if no target or sequence is available
  if (currentTarget === undefined || !currentSequence) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={factsToPractice.length > 0 ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1) : 0}
      />

      {/* Main Content Area - Centered Sequence Display */}
      <div className="flex-1 flex items-center justify-center px-6">
        {/* Ready Phase: Waiting */}
        {currentPhase === 'showing' && !showSequence && (
          <div className="text-center">
            <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">Get ready...</div>
            <div className="text-lg text-gray-500">What comes next?</div>
          </div>
        )}

        {/* Sequence Display Phase */}
        {showSequence && (
          <motion.div
            key={currentSequence.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: 1,
              scale: currentPhase === 'showing' ? [1, 1.05, 1] : 1,
            }}
            transition={{
              duration: currentPhase === 'showing' ? 2 : 0.5,
              repeat: currentPhase === 'showing' ? 1 : 0
            }}
            className="flex flex-col items-center justify-center"
          >
            {/* Number sequence display */}
            <div className="flex items-center gap-6 mb-8">
              {currentSequence.sequence.map((number, index) => (
                <motion.div
                  key={`seq-${index}`}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.2 }}
                  className="w-24 h-24 lg:w-32 lg:h-32 bg-blue-600 text-white rounded-3xl flex items-center justify-center text-5xl lg:text-6xl font-bold shadow-lg border-4 border-blue-700"
                  style={NUMERAL_FONT_STYLE}
                >
                  {number}
                </motion.div>
              ))}

              {/* Target number with special highlighting */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: 1,
                  opacity: 1,
                  boxShadow: currentPhase === 'showing' ?
                    ['0 0 0 0 rgba(34, 197, 94, 0.7)', '0 0 0 20px rgba(34, 197, 94, 0)', '0 0 0 0 rgba(34, 197, 94, 0)'] :
                    '0 20px 40px rgba(0, 0, 0, 0.1)'
                }}
                transition={{
                  duration: 0.4,
                  delay: currentSequence.sequence.length * 0.2,
                  boxShadow: { duration: 2, repeat: currentPhase === 'showing' ? 2 : 0 }
                }}
                className="w-24 h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-green-400 to-green-600 text-white rounded-3xl flex items-center justify-center text-5xl lg:text-6xl font-bold shadow-lg border-4 border-green-700"
                style={NUMERAL_FONT_STYLE}
              >
                {currentSequence.targetNumber}
              </motion.div>
            </div>

            {/* Instruction text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.8 }}
              className="text-2xl lg:text-3xl font-semibold text-gray-700 text-center"
            >
              What comes next? {currentSequence.targetNumber}!
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default WhatComesNextIDoScreen;